
import React from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Trophy, Repeat2, Plus, Pencil, EyeOff, Eye } from 'lucide-react';
import TransitionLoopFeed from "@/components/transitions/TransitionLoopFeed";
import BulletinLightbox from "@/components/bulletins/BulletinLightbox";
import BulletinMasonry from "@/features/bulletins-test/BulletinMasonry";
import { TestBulletinItem } from "@/features/bulletins-test/TestBulletinCard";
import { useLoopActions } from "@/hooks/use-loops";
import { useReblogActions, ReblogType } from "@/hooks/use-reblogs";
import { FeaturedGrid } from './FeaturedGrid';
import { useFeaturedEnabled, useToggleFeatured } from '@/hooks/use-featured';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface Badge {
  id: string;
  name: string;
  description: string;
  image_url: string;
}

interface UserBadge {
  earning_order: number;
  badges: Badge;
}

interface ProfileGalleryProps {
  userId: string;
}

const ProfileGallery = ({ userId }: ProfileGalleryProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: featuredEnabled } = useFeaturedEnabled(userId);
  const isOwnProfile = user?.id === userId;
  const toggleFeatured = useToggleFeatured();
  const [isEditingFeatured, setIsEditingFeatured] = React.useState(false);
  const [selectedMediaId, setSelectedMediaId] = React.useState<string | null>(null);
  const [mediaLikedMap, setMediaLikedMap] = React.useState<Record<string, boolean>>({});
  const mediaContentRef = React.useRef<HTMLDivElement>(null);
  const [mediaContentBounds, setMediaContentBounds] = React.useState<{ top: number; left: number; width: number; height: number } | null>(null);

  const handleAddFeatured = async () => {
    await toggleFeatured.mutateAsync(true);
    setIsEditingFeatured(true);
  };

  const handleImageError = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '';
    e.currentTarget.classList.add('bg-gray-800/60');
  }, []);

  // Fetch user badges
  const { data: userBadges, isLoading: isLoadingBadges } = useQuery({
    queryKey: ['badges', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_badges')
        .select(`
          earning_order,
          badges (
            id,
            name,
            description,
            image_url
          )
        `)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      
      const transformedData = data?.map(item => ({
        earning_order: item.earning_order,
        badges: item.badges[0]
      })) || [];
      
      return transformedData as UserBadge[];
    },
    enabled: !!userId,
  });

  // Fetch user's loops and reblogs with infinite query
  const {
    data: loopsData,
    fetchNextPage: fetchNextLoopsPage,
    hasNextPage: hasNextLoopsPage,
    isFetchingNextPage: isFetchingNextLoopsPage,
    isLoading: isLoadingLoops
  } = useInfiniteQuery({
    queryKey: ['user-loops-and-reblogs', userId],
    queryFn: async ({ pageParam = 0 }) => {
      const startIndex = pageParam * 10;
      
      try {
        // 1. First get original posts by the user
        const { data: originalPosts = [], error: originalError } = await supabase
          .from('loops')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (originalError) throw originalError;
        
        // 2. Get reblogs by the user (all types: reblog, quote, remix)
        const { data: reblogs = [], error: reblogsError } = await supabase
          .from('loops_reblogs')
          .select('*, post_id')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        
        // If loops_reblogs table doesn't exist yet, continue with just original posts
        if (reblogsError && !reblogsError.message.includes('does not exist')) {
          throw reblogsError;
        }
        
        // 3. Get the original post content for each reblog
        let rebloggedLoops: any[] = [];
        if (reblogs && reblogs.length > 0) {
          const rebloggedPostIds = reblogs.map(r => r.post_id).filter(Boolean);
          
          if (rebloggedPostIds.length > 0) {
            const { data: rebloggedPosts = [] } = await supabase
              .from('loops')
              .select('*')
              .in('id', rebloggedPostIds);
              
            // Create a map for quicker lookup
            const rebloggedPostsMap = new Map();
            for (const post of rebloggedPosts) {
              rebloggedPostsMap.set(post.id, post);
            }
            
            // Combine reblog metadata with original post content
            rebloggedLoops = reblogs.map(reblog => {
              const originalPost = rebloggedPostsMap.get(reblog.post_id);
              if (!originalPost) return null; // Skip if original post not found
              
              return {
                ...originalPost,
                reblog_data: {
                  type: reblog.type,
                  reblog_id: reblog.id,
                  content: reblog.content, // For quotes and remixes
                  created_at: reblog.created_at
                },
                is_reblogged: true
              };
            }).filter(Boolean);
          }
        }
        
        // 4. Combine original posts and reblogged posts, sort by creation time
        const allUserContent = [
          ...originalPosts.map(post => ({ ...post, is_reblogged: false })),
          ...rebloggedLoops
        ];
        
        // Sort by created date (reblogs use reblog date)
        allUserContent.sort((a, b) => {
          const dateA = a.is_reblogged ? new Date(a.reblog_data.created_at) : new Date(a.created_at);
          const dateB = b.is_reblogged ? new Date(b.reblog_data.created_at) : new Date(b.created_at);
          return dateB.getTime() - dateA.getTime();
        });
        
        // Apply pagination
        const paginatedContent = allUserContent.slice(startIndex, startIndex + 10);
        
        // 5. Get profiles for all posts
        // Get all user IDs (original authors) that we need to fetch
        const userIds = [...new Set(paginatedContent.map(loop => loop.user_id))].filter(Boolean);
        
        if (userIds.length > 0) {
          // Fetch profiles separately
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, username, profile_image_url, is_admin, is_premium, aura_points')
            .in('id', userIds);
          
          if (!profilesError && profilesData) {
            // Create profiles lookup map
            const profilesMap = new Map();
            for (const profile of profilesData) {
              profilesMap.set(profile.id, profile);
            }
            
            // Attach profile data to each loop
            for (const loop of paginatedContent) {
              loop.profiles = profilesMap.get(loop.user_id);
            }
          }
        }
        
        return {
          items: paginatedContent,
          nextPage: paginatedContent.length === 10 ? pageParam + 1 : undefined
        };
      } catch (err) {
        console.error("Error fetching user's content:", err);
        // Fallback to just original posts if there's an error with reblogs
        const { data = [], error } = await supabase
          .from('loops')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .range(startIndex, startIndex + 9);
          
        if (error) throw error;
        
        return {
          items: data || [],
          nextPage: data.length === 10 ? pageParam + 1 : undefined
        };
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: !!userId,
  });

  // Fetch user's bulletins with infinite query
  const {
    data: bulletinsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingBulletins
  } = useInfiniteQuery({
    queryKey: ['user-bulletins', userId],
    queryFn: async ({ pageParam = 0 }) => {
      const startIndex = pageParam * 10;
      const { data, error } = await supabase
        .from('bulletins')
        .select(`
          *,
          profiles:profiles(username, profile_image_url, is_admin, is_premium, combo_score)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(startIndex, startIndex + 9);

      if (error) throw error;
      
      return {
        items: data || [],
        nextPage: data?.length === 10 ? pageParam + 1 : undefined
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: !!userId,
  });

  const userBulletins = bulletinsData?.pages.flatMap(page => page.items) || [];
  const userLoops = loopsData?.pages.flatMap(page => page.items) || [];
  
  // Loop actions
  const { likeLoop, unlikeLoop } = useLoopActions();
  const { reblog, unreloop, hasReblogged, getReblogDetails } = useReblogActions();
  
  const handleLoopEngage = (loopId: string) => {
    // Handle engagement with loop
    console.log("Engaged with loop:", loopId);
  };
  
  const handleLoopLike = (loopId: string) => {
    const loop = userLoops.find(l => l.id === loopId);
    if (loop) {
      if (loop.is_liked) {
        unlikeLoop(loopId);
      } else {
        likeLoop(loopId);
      }
    }
  };
  
  const handleLoopReblog = (loopId: string) => {
    const isReblogged = hasReblogged(loopId);
    if (isReblogged) {
      const details = getReblogDetails(loopId);
      if (details?.reblog_id) {
        unreloop(details.reblog_id);
      }
    } else {
      reblog(loopId);
    }
  };
  
  const handleLoopClick = (loopId: string) => {
    navigate(`/loops?post=${loopId}`);
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Detect when user scrolls near the bottom to load more content
  React.useEffect(() => {
    const handleScroll = () => {
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 500;
      
      // Load more bulletins if on bulletins tab
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        handleLoadMore();
      }
      
      // Load more loops if on loops tab
      if (nearBottom && hasNextLoopsPage && !isFetchingNextLoopsPage) {
        handleLoadMoreLoops();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, hasNextLoopsPage, isFetchingNextLoopsPage]);
  
  const handleLoadMoreLoops = () => {
    if (hasNextLoopsPage && !isFetchingNextLoopsPage) {
      fetchNextLoopsPage();
    }
  };

  // Prime like map from fetched media bulletins
  React.useEffect(() => {
    setMediaLikedMap((prev) => {
      const next = { ...prev };
      userBulletins.forEach((bulletin: any) => {
        if (next[bulletin.id] === undefined) {
          next[bulletin.id] = !!bulletin.is_liked;
        }
      });
      return next;
    });
  }, [userBulletins]);

  React.useEffect(() => {
    const updateBounds = () => {
      if (!mediaContentRef.current) return;
      const rect = mediaContentRef.current.getBoundingClientRect();
      setMediaContentBounds({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    };
    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, []);

  const mediaItems: TestBulletinItem[] = React.useMemo(() => {
    return userBulletins
      .filter((bulletin: any) => bulletin.image_url || bulletin.thumbnail_url || bulletin.video_url)
      .map((bulletin: any) => ({
        id: bulletin.id,
        user_id: bulletin.user_id,
        image_url: bulletin.image_url,
        thumbnail_url: bulletin.thumbnail_url,
        is_relic: bulletin.is_relic ?? false,
        is_mature: bulletin.is_mature,
        created_at: bulletin.created_at,
        like_count: bulletin.like_count,
        title: bulletin.title,
        content: bulletin.content,
        content_labels: bulletin.content_labels,
        file_type: bulletin.file_type,
        image_version: bulletin.image_version,
        saved_at: bulletin.saved_at,
        expires_at: bulletin.expires_at,
        auto_category: bulletin.auto_category,
        user_category: bulletin.user_category,
      }));
  }, [userBulletins]);

  const handleMediaLike = React.useCallback(async (id: string) => {
    if (!user?.id) return;
    setMediaLikedMap((prev) => ({ ...prev, [id]: true }));
    try {
      const { error } = await supabase
        .from('bulletin_likes')
        .insert([{ bulletin_id: id, user_id: user.id }]);
      if (error && error.code !== '23505') {
        throw error;
      }
    } catch (error) {
      console.error('Failed to like bulletin', error);
      setMediaLikedMap((prev) => ({ ...prev, [id]: false }));
    }
  }, [user?.id]);

  const handleMediaOpen = React.useCallback((item: TestBulletinItem) => {
    setSelectedMediaId(item.id);
  }, []);

  const handleMediaNavigate = React.useCallback((nextId: string) => {
    setSelectedMediaId(nextId);
  }, []);

  const handleMediaClose = React.useCallback(() => {
    setSelectedMediaId(null);
  }, []);

  // Determine default tab based on featured enabled
  const defaultTab = featuredEnabled ? "featured" : "media";

  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <div className="flex items-center justify-between mb-4">
        <TabsList className="flex-1 justify-start px-0 bg-transparent border-b border-gray-800 rounded-none h-auto">
        {featuredEnabled && (
          <TabsTrigger value="featured" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent">Featured</TabsTrigger>
        )}
        <TabsTrigger value="loops" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent">Loops</TabsTrigger>
        <TabsTrigger value="media" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent">Media</TabsTrigger>
        <TabsTrigger value="badges" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent">Drops Collection</TabsTrigger>
        <TabsTrigger value="backstage" disabled className="rounded-none border-b-2 border-transparent text-gray-500 cursor-not-allowed">Backstage</TabsTrigger>
      </TabsList>
      {isOwnProfile && (
        <div className="ml-4 mb-2">
          {!featuredEnabled ? (
            <Button
              onClick={handleAddFeatured}
              size="sm"
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Featured
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:bg-white/10"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsEditingFeatured(!isEditingFeatured)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  {isEditingFeatured ? 'Exit Edit Mode' : 'Edit Featured'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await toggleFeatured.mutateAsync(false);
                  }}
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Featured Tab
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      </div>
      
      {featuredEnabled && (
        <TabsContent value="featured" className="mt-6">
          <FeaturedGrid userId={userId} isOwnProfile={isOwnProfile} isEditing={isEditingFeatured} onEditingChange={setIsEditingFeatured} />
        </TabsContent>
      )}
      
      <TabsContent value="loops" className="mt-6">
        {isLoadingLoops ? (
          <div className="text-center py-8">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-primary rounded-full animate-spin"></div>
            <p className="mt-2 text-gray-400">Loading loops...</p>
          </div>
        ) : userLoops.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No loops posted yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {userLoops.map((loop) => (
              <div key={loop.id} className="space-y-2">
                {loop.is_reblogged && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1F2937]/60 rounded-lg text-sm text-gray-300">
                    <Repeat2 size={14} className="text-gray-400" />
                    <span className="font-medium">Relooped</span>
                    {loop.reblog_data?.type === 'quote' && <span>with comment</span>}
                    {loop.reblog_data?.type === 'remix' && <span>as remix</span>}
                  </div>
                )}
                {loop.is_reblogged && loop.reblog_data?.type === 'quote' && loop.reblog_data?.content && (
                  <div className="px-3 py-2 bg-[#1F2937]/40 rounded-lg border border-white/10 text-sm text-gray-200">
                    {loop.reblog_data.content}
                  </div>
                )}
                <TransitionLoopFeed
                  loop={{ ...loop, is_reblogged_by_you: !!loop.is_reblogged || hasReblogged(loop.id) }}
                  onEngage={() => handleLoopEngage(loop.id)}
                  isEngaged={!!loop.is_liked}
                  onPostClick={() => handleLoopClick(loop.id)}
                  onLike={() => handleLoopLike(loop.id)}
                  onReblog={() => handleLoopReblog(loop.id)}
                  onComment={() => handleLoopClick(loop.id)}
                  onQuote={() => {}}
                  onMoreActions={() => {}}
                  className="h-full"
                />
              </div>
            ))}
            {isFetchingNextLoopsPage && (
              <div className="col-span-full text-center py-4">
                <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-primary rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        )}
      </TabsContent>
      
      <TabsContent value="media" className="mt-6">
        {isLoadingBulletins ? (
          <div className="text-center py-8">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-primary rounded-full animate-spin"></div>
            <p className="mt-2 text-gray-400">Loading media...</p>
          </div>
        ) : mediaItems.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>No media posts yet</p>
          </div>
        ) : (
          <div ref={mediaContentRef}>
            <BulletinMasonry
              items={mediaItems}
              likedMap={mediaLikedMap}
              onLike={handleMediaLike}
              onOpen={handleMediaOpen}
            />

            {isFetchingNextPage && (
              <div className="text-center py-4 mt-4">
                <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-primary rounded-full animate-spin"></div>
              </div>
            )}

            {selectedMediaId && (
              <BulletinLightbox
                bulletins={userBulletins.filter((bulletin: any) => bulletin.image_url || bulletin.thumbnail_url || bulletin.video_url)}
                selectedId={selectedMediaId}
                onNavigate={handleMediaNavigate}
                onClose={handleMediaClose}
                contentBounds={mediaContentBounds}
                preloadedThumbnail={mediaItems.find((item) => item.id === selectedMediaId)?.thumbnail_url || null}
              />
            )}
          </div>
        )}
      </TabsContent>
      
      <TabsContent value="backstage">
        <div className="text-center py-8 text-gray-400">Backstage content coming soon</div>
      </TabsContent>
      
      <TabsContent value="badges" className="mt-6">
        {isLoadingBadges ? (
          <div className="text-center py-8">Loading badges...</div>
        ) : !userBadges?.length ? (
          <div className="text-center py-8 text-gray-400">
            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No Drops Collected Yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {userBadges.map((userBadge) => (
              <Card key={userBadge.badges.id} className="bg-[#1E2330] border-none overflow-hidden group">
                <div className="relative aspect-square">
                  <img 
                    src={userBadge.badges.image_url} 
                    alt={userBadge.badges.name} 
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                  />
                  <div className="absolute top-2 right-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded-full">
                    #{userBadge.earning_order}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 p-4 w-full">
                      <h3 className="text-lg font-bold">{userBadge.badges.name}</h3>
                      <p className="text-sm text-gray-300 mt-1">{userBadge.badges.description}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default ProfileGallery;
